import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { makeStyles } from "@material-ui/core";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import { PlusIcon, MinusIcon, MagnifyingGlassIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import CustomColorPicker from "pages/flyer/builder/drawer/components/CustomColorPicker";
import { useIsBuilderEmptyState } from "pages/flyer/builder/hooks/useIsBuilderEmptyState";
import { AssetRecordType, TLShapeId, useEditor } from "@tldraw/tldraw";
import { useBrandKitStore, MAX_BRAND_COLORS } from "store/brandKit";
import { useStore } from "store";
import { useColorPickerContext } from "pages/flyer/builder/drawer/components/ColorPickerContext";
import { useDraggableColorPicker } from "pages/flyer/builder/drawer/components/useDraggableColorPicker";

import { getOpacityPercentage } from "pages/flyer/builder/utils";
import { s3CustomFontService, FontCDNData } from "pages/flyer/builder/services/s3CustomFontService";
import { customFontStorage } from "pages/flyer/builder/services/customFontStorage";
import { uploadBrandKitAsset } from "pages/flyer/builder/services/brandKitUploadService";
import {
  BrandKitFontPreset,
  SaveBrandKitFontPresetInput,
  UpdateBrandKitFontPresetInput,
} from "graphQL/mutations/brandKit";
import AddBrandFontModal from "./AddBrandFontModal";
import BrandFontPresetRow from "./BrandFontPresetRow";
import BrandFontsQueueDialog, { BrandFontsQueueFamily } from "./BrandFontsQueueDialog";
import BrandKitDropZone from "./BrandKitDropZone";
import BrandKitGallery from "./BrandKitGallery";
import { useImageUpload } from "pages/flyer/builder/drawer/contexts/ImageUploadContext";
import { SnackBarContext } from "components/snack-bar";
import { get } from "idb-keyval";
import { getSignedS3Url } from "pages/flyer/builder/utils/getSignedS3Url";
import "pages/flyer/builder/drawer/components/styles.css";

// ─── DragHandle + SortableFontPresetRow ───────────────────────────────────────

const DragHandleIcon = () => (
  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="2.5" cy="2" r="1.5" fill="#B0B0C0" />
    <circle cx="7.5" cy="2" r="1.5" fill="#B0B0C0" />
    <circle cx="2.5" cy="7" r="1.5" fill="#B0B0C0" />
    <circle cx="7.5" cy="7" r="1.5" fill="#B0B0C0" />
    <circle cx="2.5" cy="12" r="1.5" fill="#B0B0C0" />
    <circle cx="7.5" cy="12" r="1.5" fill="#B0B0C0" />
  </svg>
);

const SortableFontPresetRow: React.FC<{
  preset: BrandKitFontPreset;
  onApply: (preset: BrandKitFontPreset) => void;
  onEdit: (preset: BrandKitFontPreset, anchorEl: HTMLElement) => void;
  onRemove: (id: string) => void;
  onUpdateLabel: (id: string, label: string) => void;
}> = ({ preset, onApply, onEdit, onRemove, onUpdateLabel }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: preset.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        alignItems: "center",
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
        background: isDragging ? "#F0F0FF" : undefined,
        borderRadius: isDragging ? 4 : undefined,
      }}
    >
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: "grab",
          display: "flex",
          alignItems: "center",
          padding: "0 4px 0 0",
          flexShrink: 0,
        }}
      >
        <DragHandleIcon />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <BrandFontPresetRow
          preset={preset}
          onApply={onApply}
          onEdit={onEdit}
          onRemove={onRemove}
          onUpdateLabel={onUpdateLabel}
        />
      </div>
    </div>
  );
};

// ─── BrandColorRow ────────────────────────────────────────────────────────────

interface BrandColorRowProps {
  id: string;
  hexValue: string;
  label: string | null;
  onUpdate: (id: string, hexValue: string) => void;
  onRemove: (id: string) => void;
}

const BrandColorRow = ({ id, hexValue, label, onUpdate, onRemove }: BrandColorRowProps) => {
  const classes = useStyles();
  const editor = useEditor();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentColor, setCurrentColor] = useState(hexValue);
  const [opacity, setOpacity] = useState(() => getOpacityPercentage(hexValue));

  const { openColorPicker, closeColorPicker, isOpen } = useColorPickerContext();
  const pickerId = useMemo(() => `brand-color-${id}`, [id]);

  useEffect(() => {
    setCurrentColor(hexValue);
    setOpacity(getOpacityPercentage(hexValue));
  }, [hexValue]);

  // Close if another picker opens
  useEffect(() => {
    if (!isOpen(pickerId) && anchorEl) {
      setAnchorEl(null);
    }
  }, [isOpen, pickerId, anchorEl]);

  const open = Boolean(anchorEl) && isOpen(pickerId);

  const handleSwatchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    openColorPicker(pickerId);
  };

  const handleClose = () => {
    if (currentColor !== hexValue) {
      onUpdate(id, currentColor);
    }
    setAnchorEl(null);
    closeColorPicker(pickerId);
  };

  const handleColorChange = useCallback((color: string) => {
    const c = color.toLowerCase();
    setCurrentColor(c);
    setOpacity(getOpacityPercentage(c));
  }, []);

  // Shared canvas-pannable + drag-by-border behaviour. The Paper's
  // free border (the p-4 padding around CustomColorPicker) is the
  // drag handle; clicks on swatches/inputs pass through normally.
  const { paperContentRef, handleBorderMouseDown, isDragging } = useDraggableColorPicker(open, anchorEl, handleClose);

  const handleColorInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentColor(e.target.value);
  };

  const handleColorInputBlur = () => {
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(currentColor)) {
      setOpacity(getOpacityPercentage(currentColor));
      onUpdate(id, currentColor);
    } else {
      setCurrentColor(hexValue);
      setOpacity(getOpacityPercentage(hexValue));
    }
  };

  return (
    <div className={classes.colorRow}>
      <div
        className="flex items-center w-full py-1 px-2 rounded-[4px] relative"
        style={{ border: "1px solid #E2E2FC" }}
      >
        <button className={classes.swatchBtn} style={{ backgroundColor: currentColor }} onClick={handleSwatchClick} />
        <input
          type="text"
          value={currentColor.toUpperCase()}
          onChange={handleColorInput}
          onBlur={handleColorInputBlur}
          className="h-6 text-[10px] rounded-[4px] w-[55px] px-1 mr-1 flex items-center justify-center text-left border-none outline-none bg-transparent"
          title={label || currentColor}
        />
        <div
          className="absolute h-4 bg-[#E2E2FC]"
          style={{ left: "70%", width: "1px", top: "50%", transform: "translateY(-50%)" }}
        />
        <span className="ml-auto text-[10px] text-[#2E3860] pr-1">{opacity}%</span>
      </div>

      <MinusIcon className={classes.minusIcon} onClick={() => onRemove(id)} />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        // Canvas-pannable plumbing — `pointerEvents:none` on the
        // Popover root + `hideBackdrop` + `disableScrollLock` lets
        // tldraw receive the mousedown / wheel events that drive pan
        // and zoom while this picker is open. The Paper itself re-
        // enables pointer events below. Outside-click closing is
        // handled by `useDraggableColorPicker`.
        style={{ pointerEvents: "none" }}
        hideBackdrop
        disableScrollLock
        // Baseline `translateX(8px)` is kept and the drag offset is
        // layered on top via the `--cp-drag-x` / `--cp-drag-y` CSS
        // variables that the hook sets directly on the Paper.
        PaperProps={{
          style: {
            pointerEvents: "auto",
            borderRadius: "8px",
            transform: "translate(calc(8px + var(--cp-drag-x, 0px)), var(--cp-drag-y, 0px))",
          },
        }}
      >
        <div
          ref={paperContentRef}
          onMouseDown={handleBorderMouseDown}
          className="p-4 w-[264px] rounded-[8px] h-[442px] overflow-y-auto"
          // alignContent centers the inner picker vertically; see
          // ColorPickerPopover for the rationale.
          style={{ cursor: isDragging ? "grabbing" : "grab", alignContent: "center" }}
        >
          <CustomColorPicker
            editor={editor}
            shapeColor={currentColor}
            showColorPickerSetMode
            onColorChange={handleColorChange}
          />
        </div>
      </Popover>
    </div>
  );
};

// ─── Font family grouping ─────────────────────────────────────────────────────

const WEIGHT_STYLE_KEYWORDS = new Set([
  "thin",
  "hairline",
  "extralight",
  "ultralight",
  "light",
  "regular",
  "normal",
  "reg",
  "re",
  "medium",
  "md",
  "semibold",
  "demibold",
  "sb",
  "bold",
  "bd",
  "bld",
  "extrabold",
  "ultrabold",
  "eb",
  "black",
  "heavy",
  "bk",
  "hv",
  "italic",
  "it",
  "oblique",
  "condensed",
  "cond",
  "cn",
  "semicn",
]);

/** Check if a lowercase string is entirely composed of weight/style keywords. */
function isAllKeywords(str: string): boolean {
  let remaining = str;
  let matched = false;
  while (remaining.length > 0) {
    let found = false;
    for (const kw of Array.from(WEIGHT_STYLE_KEYWORDS)) {
      if (remaining.startsWith(kw)) {
        remaining = remaining.slice(kw.length);
        found = true;
        matched = true;
        break;
      }
    }
    if (!found) break;
  }
  return matched && remaining.length === 0;
}

/** Extract base font family name by stripping weight/style suffixes.
 *  "AdobeClean-BoldItalic" → "AdobeClean"
 *  "UberMove-Medium" → "UberMove"
 *  "UberMoveTextMedium" → "UberMoveText"
 *  "Roboto" → "Roboto"
 */
function getBaseFontFamily(name: string): string {
  // 1) Try splitting on last hyphen
  const hyphenIdx = name.lastIndexOf("-");
  if (hyphenIdx > 0) {
    const suffix = name
      .slice(hyphenIdx + 1)
      .toLowerCase()
      .replace(/\s*\d+$/, "");
    if (isAllKeywords(suffix)) {
      return name.slice(0, hyphenIdx);
    }
  }

  // 2) Try splitting on last underscore
  const underscoreIdx = name.lastIndexOf("_");
  if (underscoreIdx > 0) {
    const suffix = name
      .slice(underscoreIdx + 1)
      .toLowerCase()
      .replace(/\s*\d+$/, "");
    if (isAllKeywords(suffix)) {
      return name.slice(0, underscoreIdx);
    }
  }

  // 3) Try splitting on last space — handles "uberMove Text Medium" style names.
  //    Walk backwards through space-separated words: if the trailing words are
  //    all weight/style keywords, strip them.
  const spaceIdx = name.lastIndexOf(" ");
  if (spaceIdx > 0) {
    const words = name.split(" ");
    let trailingKeywords = 0;
    for (let i = words.length - 1; i > 0; i--) {
      const w = words[i].toLowerCase().replace(/\d+$/, "");
      if (w && isAllKeywords(w)) {
        trailingKeywords++;
      } else {
        break;
      }
    }
    if (trailingKeywords > 0) {
      return words
        .slice(0, words.length - trailingKeywords)
        .join(" ")
        .trim();
    }
  }

  // 4) Handle concatenated names like "UberMoveTextMedium"
  //    Walk backwards from the end to find the longest trailing keyword sequence
  const lower = name.toLowerCase().replace(/\s*\d+$/, "");
  // Sort keywords longest-first so "extrabold" matches before "extra" or "bold"
  const sortedKeywords = Array.from(WEIGHT_STYLE_KEYWORDS).sort((a, b) => b.length - a.length);
  let endPos = lower.length;
  let strippedAny = false;

  while (endPos > 0) {
    let found = false;
    for (const kw of sortedKeywords) {
      if (endPos >= kw.length && lower.slice(endPos - kw.length, endPos) === kw) {
        endPos -= kw.length;
        strippedAny = true;
        found = true;
        break;
      }
    }
    if (!found) break;
  }

  if (strippedAny && endPos > 0) {
    return name.slice(0, endPos);
  }

  return name;
}

/** Normalize a family name for grouping: strip spaces/separators, lowercase. */
function normalizeFamily(name: string): string {
  return name.replace(/[\s\-_]+/g, "").toLowerCase();
}

/** Normalize a family name for display: strip spaces/separators to produce
 *  a clean CamelCase name. "uberMove Text" → "uberMoveText".
 *  Ensures consistent display regardless of which file is processed first.
 */
function normalizeDisplayName(name: string): string {
  return name.replace(/[\s\-_]+/g, "");
}

interface FontFamily {
  baseName: string;
  variants: FontCDNData[];
}

function groupFontsByFamily(fonts: FontCDNData[]): FontFamily[] {
  const map = new Map<string, FontCDNData[]>();
  const displayName = new Map<string, string>();
  for (const font of fonts) {
    const base = getBaseFontFamily(font.name);
    const key = normalizeFamily(base);
    if (!map.has(key)) {
      map.set(key, []);
      displayName.set(key, normalizeDisplayName(base));
    }
    map.get(key)!.push(font);
  }
  return Array.from(map.entries())
    .map(([key, variants]) => ({ baseName: displayName.get(key)!, variants }))
    .sort((a, b) => a.baseName.localeCompare(b.baseName));
}

// ─── BrandFontsSection ────────────────────────────────────────────────────────

const BrandFontsSection = () => {
  const classes = useStyles();
  const editor = useEditor();
  const runSnackBar = useContext(SnackBarContext);
  const [cdnFonts, setCdnFonts] = useState<FontCDNData[]>([]);
  const [fontsLoading, setFontsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fontSearchTerm, setFontSearchTerm] = useState("");
  const [defaultFontNames, setDefaultFontNames] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Brand Fonts queue — after a (possibly multi-file) upload, each detected
  // family is listed and can be added to the Brand Kit or skipped.
  const [brandFontQueueOpen, setBrandFontQueueOpen] = useState(false);
  const [brandFontQueue, setBrandFontQueue] = useState<BrandFontsQueueFamily[]>([]);

  // Font presets from store
  const { fontPresets, addFontPreset, updateFontPreset, removeFontPreset, reorderFontPresets, loadBrandKit } =
    useBrandKitStore();

  // DnD sensors for font preset reorder
  const presetSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handlePresetDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = fontPresets.findIndex((p: BrandKitFontPreset) => p.id === active.id);
      const newIndex = fontPresets.findIndex((p: BrandKitFontPreset) => p.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(fontPresets, oldIndex, newIndex);
      const orderedIds = reordered.map((p: BrandKitFontPreset) => p.id);
      reorderFontPresets(orderedIds);

      // First preset becomes default font for new text shapes
      if (reordered.length > 0) {
        const p = reordered[0];
        try {
          localStorage.setItem("lastSelectedFont", p.fontFamily);
          localStorage.setItem(
            "brandDefaultFontPreset",
            JSON.stringify({
              fontFamily: p.fontFamily,
              fontVariant: p.fontVariant,
              fontSize: p.fontSize,
              lineHeight: p.lineHeight,
              letterSpacing: p.letterSpacing,
            })
          );
        } catch {
          // ignore localStorage errors
        }
      }
    },
    [fontPresets, reorderFontPresets]
  );

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAnchor, setModalAnchor] = useState<HTMLElement | null>(null);
  const [editingPreset, setEditingPreset] = useState<BrandKitFontPreset | null>(null);
  const [selectedFamilyForModal, setSelectedFamilyForModal] = useState<{
    baseName: string;
    variants: FontCDNData[];
  } | null>(null);

  // Group fonts by family (e.g. AdobeClean-Bold + AdobeClean-Light → "AdobeClean")
  const fontFamilies = useMemo(() => groupFontsByFamily(cdnFonts), [cdnFonts]);

  // Load default font names (Google + static) from IndexedDB cache
  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const cached = await get("fonts");
        if (cached && Array.isArray(cached)) {
          setDefaultFontNames(cached);
        }
      } catch (err) {
        console.error("Failed to load cached font names:", err);
      }
    };
    loadDefaults();
  }, []);

  // CSS weight variants for standard/Google fonts (same as right sidebar)
  const cssWeightVariants = useMemo(
    () => [
      { suffix: "", weight: 400 }, // Regular
      { suffix: "Light", weight: 300 },
      { suffix: "SemiBold", weight: 600 },
      { suffix: "Bold", weight: 700 },
    ],
    []
  );

  // Combined font families: custom CDN fonts + standard fonts (with CSS weight variants)
  const allFontFamilies = useMemo(() => {
    const customBaseNames = new Set(fontFamilies.map((f) => f.baseName.toLowerCase()));
    const defaultFamilies = defaultFontNames
      .filter((name) => !customBaseNames.has(name.toLowerCase()))
      .map((name) => ({
        baseName: name,
        variants: cssWeightVariants.map(
          ({ suffix }) =>
            ({
              name: suffix ? `${name}-${suffix}` : name,
              s3Url: "",
            } as FontCDNData)
        ),
      }));

    return [...fontFamilies, ...defaultFamilies].sort((a, b) => a.baseName.localeCompare(b.baseName));
  }, [fontFamilies, defaultFontNames, cssWeightVariants]);

  // Load fonts from registry (same source as TextParameters)
  const loadFonts = useCallback(async () => {
    setFontsLoading(true);
    try {
      // Clear the service's in-memory cache so we get fresh data after uploads
      s3CustomFontService.clearCache();
      const fonts = await s3CustomFontService.getLocalFontRegistry();
      setCdnFonts(fonts);
    } catch (err) {
      console.error("Failed to load brand kit fonts:", err);
    } finally {
      setFontsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFonts();
  }, [loadFonts]);

  // Keep a ref to allFontFamilies so the event handler always sees the latest value
  const allFontFamiliesRef = useRef(allFontFamilies);
  allFontFamiliesRef.current = allFontFamilies;

  // Listen for "expand-brand-kit-fonts" event (dispatched with delay from Tldraw after sidebar mounts)
  useEffect(() => {
    const openModalForFont = (fontName: string, retries = 0) => {
      const families = allFontFamiliesRef.current;
      const family = families.find((f) => f.baseName === fontName || f.variants.some((v) => v.name === fontName));
      if (family) {
        setSelectedFamilyForModal(family);
        setEditingPreset(null);
        setModalAnchor(dropdownRef.current);
        setModalOpen(true);
      } else if (retries < 5) {
        // Google fonts data may still be loading, retry after a short delay
        setTimeout(() => openModalForFont(fontName, retries + 1), 300);
      }
    };

    const handleExpandFonts = (e: Event) => {
      setExpanded(true);
      loadFonts();

      const fontName = (e as CustomEvent).detail?.fontName || "";
      if (fontName) {
        openModalForFont(fontName);
      }
    };
    window.addEventListener("expand-brand-kit-fonts", handleExpandFonts);
    return () => window.removeEventListener("expand-brand-kit-fonts", handleExpandFonts);
  }, [loadFonts]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // Open modal with selected font family
  const handleFontSelect = useCallback((family: FontFamily) => {
    setDropdownOpen(false);
    setSelectedFamilyForModal(family);
    setEditingPreset(null);
    setModalAnchor(dropdownRef.current);
    setModalOpen(true);
  }, []);

  // Load font variant into document.fonts (CDN custom fonts only; standard fonts loaded via CSS)
  const loadFontVariant = useCallback(
    async (variantName: string) => {
      const existing = Array.from(document.fonts).find((f) => f.family.toLowerCase() === variantName.toLowerCase());
      if (existing && existing.status === "loaded") return;
      const cdnFont = cdnFonts.find((f) => f.name === variantName);
      if (cdnFont) {
        try {
          const fontFace = new FontFace(variantName, `url(${cdnFont.s3Url})`);
          await fontFace.load();
          document.fonts.add(fontFace);
        } catch (err) {
          console.error(`Failed to load font "${variantName}":`, err);
        }
      }
    },
    [cdnFonts]
  );

  // Check if a variant is a custom CDN font (vs standard/Google font)
  const isCustomVariant = useCallback(
    (variantName: string) => {
      return cdnFonts.some((f) => f.name === variantName);
    },
    [cdnFonts]
  );

  // Parse CSS fontWeight from a standard font variant name (e.g. "Roboto-Bold" → 700)
  const getWeightFromVariant = useCallback((variantName: string, familyName: string) => {
    const suffix = variantName.slice(familyName.length).replace(/^[-_]/, "").toLowerCase();
    if (!suffix || suffix === "regular" || suffix === "normal") return 400;
    if (suffix === "light") return 300;
    if (suffix === "semibold" || suffix === "semi-bold") return 600;
    if (suffix === "bold") return 700;
    return 400;
  }, []);

  // Save a new font preset
  const handleSavePreset = useCallback(
    async (input: SaveBrandKitFontPresetInput) => {
      await addFontPreset(input);
      // Collapse the font dropdown + drop zone section once a preset has been
      // added (from an existing font). The user can re-open it with the "+".
      setExpanded(false);
    },
    [addFontPreset]
  );

  // Update an existing font preset and propagate changes to matching shapes
  const handleUpdatePreset = useCallback(
    async (input: UpdateBrandKitFontPresetInput) => {
      // Capture old values before DB update
      const oldPreset = fontPresets.find((p) => p.id === input.id);

      await updateFontPreset(input);

      // Update all shapes on the canvas that match the old preset values
      if (editor && oldPreset) {
        const newVariantRaw = input.fontVariant ?? oldPreset.fontVariant;
        const newFamily = input.fontFamily ?? oldPreset.fontFamily;

        // Parse italic/underline flags from old and new variants
        const oldParts = oldPreset.fontVariant.split(":");
        const oldBaseVariant = oldParts[0];
        const newParts = newVariantRaw.split(":");
        const newBaseVariant = newParts[0];
        const newItalic = newParts.includes("i");
        const newUnderline = newParts.includes("u");

        const oldIsCustom = isCustomVariant(oldBaseVariant);
        const newIsCustom = isCustomVariant(newBaseVariant);

        if (newIsCustom && input.fontVariant && newBaseVariant !== oldBaseVariant) {
          await loadFontVariant(newBaseVariant);
        }

        // Determine what the old font prop was on shapes
        const oldFont = oldIsCustom ? oldBaseVariant : oldPreset.fontFamily;
        const newFont = newIsCustom ? newBaseVariant : newFamily;
        const newWeight = newIsCustom ? undefined : getWeightFromVariant(newBaseVariant, newFamily);

        editor.getCurrentPageShapes().forEach((shape) => {
          if (shape.type !== "customText") return;
          const props = (shape as any).props;
          if (
            props.font === oldFont &&
            props.size === oldPreset.fontSize &&
            props.lineHeight === oldPreset.lineHeight &&
            props.letterSpacing === oldPreset.letterSpacing
          ) {
            const newProps: any = {
              ...props,
              font: newFont,
              size: input.fontSize ?? oldPreset.fontSize,
              lineHeight: input.lineHeight ?? oldPreset.lineHeight,
              letterSpacing: input.letterSpacing ?? oldPreset.letterSpacing,
              italic: newItalic ? "italic" : "not-italic",
              underline: newUnderline ? "underline" : "none",
            };
            if (newWeight !== undefined) {
              newProps.fontWeight = newWeight;
            }
            editor.updateShape({ id: shape.id, type: shape.type, props: newProps });
          }
        });
      }
    },
    [updateFontPreset, fontPresets, editor, loadFontVariant, isCustomVariant, getWeightFromVariant]
  );

  // Edit a preset (reopen modal). Looks up the preset's family in the font
  // list with three fallbacks so the modal always has something to show even
  // right after an upload (when cdnFonts might still be stale):
  //   1. Strict match on baseName
  //   2. Lenient match via normalizeFamily (handles case/separator drift)
  //   3. Refresh fonts and retry
  //   4. Synthesize a FontFamily from the preset as a last resort
  const handleEditPreset = useCallback(
    async (preset: BrandKitFontPreset, anchorEl: HTMLElement) => {
      const findFamily = (): FontFamily | null => {
        const families = allFontFamiliesRef.current;
        const strict = families.find((f) => f.baseName === preset.fontFamily);
        if (strict) return strict;
        const targetKey = normalizeFamily(preset.fontFamily);
        const lenient = families.find((f) => normalizeFamily(f.baseName) === targetKey);
        return lenient || null;
      };

      let family = findFamily();
      if (!family) {
        try {
          await loadFonts();
          // Yield a tick so the updated allFontFamilies propagates into the ref.
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
        } catch (err) {
          console.error("Failed to reload fonts for preset edit:", err);
        }
        family = findFamily();
      }
      if (!family) {
        const firstVariantName = preset.fontVariant ? preset.fontVariant.split(":")[0] : preset.fontFamily;
        family = {
          baseName: preset.fontFamily,
          variants: [{ name: firstVariantName, s3Url: "" } as FontCDNData],
        };
      }

      setEditingPreset(preset);
      setSelectedFamilyForModal(family);
      setModalAnchor(anchorEl);
      setModalOpen(true);
    },
    [loadFonts]
  );

  // Apply preset to selected text shapes
  const handleApplyPreset = useCallback(
    async (preset: BrandKitFontPreset) => {
      if (!editor) return;

      // Parse italic/underline flags from fontVariant (e.g. "Roboto-Bold:i:u")
      const variantParts = preset.fontVariant.split(":");
      const baseVariant = variantParts[0];
      const applyItalic = variantParts.includes("i");
      const applyUnderline = variantParts.includes("u");

      const isCustom = isCustomVariant(baseVariant);
      if (isCustom) {
        await loadFontVariant(baseVariant);
      }

      // For custom fonts: font = variant name (e.g. "AdobeClean-Bold")
      // For standard fonts: font = family name (e.g. "Roboto"), fontWeight = CSS weight
      const fontName = isCustom ? baseVariant : preset.fontFamily;
      const fontWeight = isCustom ? undefined : getWeightFromVariant(baseVariant, preset.fontFamily);

      editor.getSelectedShapes().forEach((shape) => {
        if (shape.type === "customText") {
          const newProps: any = {
            ...(shape as any).props,
            font: fontName,
            size: preset.fontSize,
            lineHeight: preset.lineHeight,
            letterSpacing: preset.letterSpacing,
            italic: applyItalic ? "italic" : "not-italic",
            underline: applyUnderline ? "underline" : "none",
          };
          if (fontWeight !== undefined) {
            newProps.fontWeight = fontWeight;
          }
          editor.updateShape({ id: shape.id, type: shape.type, props: newProps });
        }
      });
    },
    [editor, loadFontVariant, isCustomVariant, getWeightFromVariant]
  );

  // Remove a preset
  const handleRemovePreset = useCallback(
    async (id: string) => {
      await removeFontPreset(id);
    },
    [removeFontPreset]
  );

  // Update preset label (double-click rename)
  const handleUpdatePresetLabel = useCallback(
    async (id: string, label: string) => {
      await updateFontPreset({ id, label });
    },
    [updateFontPreset]
  );

  // Upload new font (same flow as TextParameters handleFontUpload)
  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const fontName = file.name.split(".")[0];
        if (!fontName) return;

        // Load font immediately for preview
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsDataURL(file);
        });
        const fontFace = new FontFace(fontName, `url(${dataUrl})`);
        try {
          await fontFace.load();
          document.fonts.add(fontFace);
        } catch {
          // preview failed, continue with upload
        }

        // Upload to S3 CDN
        await s3CustomFontService.uploadFontToCDN(file, fontName);

        // Reload font list
        await loadFonts();

        // Notify TextParameters to refresh its font dropdown
        window.dispatchEvent(new Event("custom-fonts-updated"));

        // Success snackbar intentionally omitted — the queue dialog that
        // opens right after already signals a successful upload.
      } catch (err) {
        console.error("Font upload failed:", err);
        runSnackBar({
          msg: "Failed to upload font. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        setUploading(false);
      }
    },
    [loadFonts, runSnackBar]
  );

  // Batched font upload: upload all files, then open the Brand Fonts queue
  // dialog with every detected family so the user can add each one (or skip).
  const handleUploadFontFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setUploading(true);
      const uploadedNames: string[] = [];
      try {
        for (const file of files) {
          const fontName = file.name.split(".")[0];
          if (!fontName) continue;

          // Immediate preview (best-effort, does not block upload)
          try {
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(new Error("Failed to read file"));
              reader.readAsDataURL(file);
            });
            const fontFace = new FontFace(fontName, `url(${dataUrl})`);
            try {
              await fontFace.load();
              document.fonts.add(fontFace);
            } catch {
              // preview failed — continue with upload
            }
          } catch {
            // reading failed — still try to upload
          }

          try {
            // 1. Upload to the font CDN so it appears in font dropdowns.
            await s3CustomFontService.uploadFontToCDN(file, fontName);
            // 2. Register as a Brand Kit asset so it is persisted in the
            //    brand kit and visible to every consumer of the store.
            try {
              await uploadBrandKitAsset(file, "font");
            } catch (assetErr) {
              console.error(`Brand kit asset save failed for "${fontName}":`, assetErr);
            }
            uploadedNames.push(fontName);
          } catch (err) {
            console.error(`Font upload failed for "${fontName}":`, err);
            runSnackBar({
              msg: `Failed to upload "${fontName}".`,
              type: "error",
              vertical: "top",
              horizontal: "center",
            });
          }
        }

        if (uploadedNames.length > 0) {
          await loadFonts();
          // Refresh the Brand Kit store so the newly persisted font assets
          // are visible everywhere that reads from the store.
          try {
            await loadBrandKit();
          } catch (bkErr) {
            console.error("Failed to reload brand kit after font upload:", bkErr);
          }
          window.dispatchEvent(new Event("custom-fonts-updated"));
          // Success snackbar intentionally omitted — the queue dialog that
          // opens right after already signals a successful upload.

          // Group uploaded files by detected family and open the queue dialog.
          const groups = new Map<string, { display: string; variants: string[] }>();
          uploadedNames.forEach((fontName) => {
            const base = getBaseFontFamily(fontName);
            const key = normalizeFamily(base);
            if (!groups.has(key)) {
              groups.set(key, { display: normalizeDisplayName(base), variants: [] });
            }
            groups.get(key)!.variants.push(fontName);
          });
          const entries: BrandFontsQueueFamily[] = Array.from(groups.values()).map((g) => ({
            baseName: g.display,
            variantNames: g.variants,
          }));
          if (entries.length > 0) {
            setBrandFontQueue(entries);
            setBrandFontQueueOpen(true);
          }
        }
      } finally {
        setUploading(false);
      }
    },
    [loadFonts, runSnackBar]
  );

  const handlePlusClick = () => {
    setExpanded(!expanded);
    if (!expanded) loadFonts();
  };

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <span className={classes.sectionLabel}>Fonts</span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Tooltip
            title="Select your preferred font and set the order. The first font will be used as default for new text."
            arrow
            placement="top"
            classes={{ tooltip: classes.customTooltip, arrow: classes.customTooltipArrow }}
          >
            <InfoCircledIcon style={{ width: 14, height: 14, color: "#2E3860", cursor: "pointer" }} />
          </Tooltip>
          <PlusIcon
            className={classes.plusIcon}
            onClick={handlePlusClick}
            style={{ transform: expanded ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
          />
        </div>
      </div>

      {expanded && (
        <>
          {/* Font dropdown */}
          <div ref={dropdownRef} className={classes.dropdownWrapper}>
            <button
              className={classes.dropdownBtn}
              onClick={() => {
                const opening = !dropdownOpen;
                setDropdownOpen(opening);
                if (opening) {
                  setFontSearchTerm("");
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }
              }}
              disabled={fontsLoading || uploading}
            >
              <span>{fontsLoading ? "Loading..." : "Select a font"}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="5"
                viewBox="0 0 9 5"
                fill="none"
                className={classes.chevron}
                style={{ transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                <path
                  d="M0.75 0.75L4.08333 4.08333L7.41667 0.75"
                  stroke="#2E3860"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {dropdownOpen && allFontFamilies.length > 0 && (
              <div className={classes.dropdownList}>
                <div style={{ padding: "4px 6px", position: "sticky", top: 0, background: "white", zIndex: 1 }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search fonts..."
                    value={fontSearchTerm}
                    onChange={(e) => setFontSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "4px 6px",
                      fontSize: 10,
                      border: "1px solid #E2E2FC",
                      borderRadius: 4,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                {(() => {
                  // Split the filtered list into two groups:
                  //  - Custom Fonts: CDN-uploaded families not already tied
                  //    to a brand preset (i.e. the user uploaded them but
                  //    hasn't added them to the Brand Kit yet).
                  //  - Other fonts: default / Google families.
                  const term = fontSearchTerm.toLowerCase();
                  const customBaseNames = new Set(fontFamilies.map((f) => f.baseName.toLowerCase()));
                  const presetBaseNames = new Set(
                    fontPresets.map((p: BrandKitFontPreset) => getBaseFontFamily(p.fontFamily).toLowerCase())
                  );
                  // Hide non-custom (default / Google) families that are
                  // already in use as a Brand Kit preset — they shouldn't
                  // be re-selectable while they're an active brand font.
                  // Custom-uploaded families stay visible even when already
                  // a preset, so the user can re-pick a specific variant.
                  const filtered = allFontFamilies.filter((f) => {
                    if (!f.baseName.toLowerCase().includes(term)) return false;
                    const base = f.baseName.toLowerCase();
                    const isCustomUpload = customBaseNames.has(base);
                    const isBrandPreset = presetBaseNames.has(base);
                    if (!isCustomUpload && isBrandPreset) return false;
                    return true;
                  });
                  const customGroup: typeof filtered = [];
                  const otherGroup: typeof filtered = [];
                  filtered.forEach((family) => {
                    const base = family.baseName.toLowerCase();
                    if (customBaseNames.has(base) && !presetBaseNames.has(base)) {
                      customGroup.push(family);
                    } else {
                      otherGroup.push(family);
                    }
                  });

                  const headerStyle: React.CSSProperties = {
                    padding: "4px 8px",
                    fontSize: 9,
                    fontWeight: 600,
                    color: "#798498",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontFamily: "Inter, sans-serif",
                  };
                  const badgeStyle: React.CSSProperties = {
                    fontSize: 9,
                    color: "#999",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    padding: "1px 5px",
                    marginLeft: 8,
                    lineHeight: "14px",
                    whiteSpace: "nowrap",
                    fontFamily: "sans-serif",
                    flexShrink: 0,
                  };
                  const renderRow = (family: (typeof filtered)[number], showCustomBadge: boolean) => (
                    <button
                      key={family.baseName}
                      className={classes.dropdownItem}
                      onClick={() => handleFontSelect(family)}
                      title={family.variants.length > 1 ? `${family.variants.length} variants` : undefined}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {family.baseName}
                      </span>
                      {showCustomBadge && <span style={badgeStyle}>Custom</span>}
                    </button>
                  );

                  return (
                    <>
                      {customGroup.length > 0 && (
                        <>
                          <div style={headerStyle}>Custom Fonts</div>
                          {customGroup.map((f) => renderRow(f, true))}
                          {otherGroup.length > 0 && (
                            <div style={{ height: 1, background: "#E2E2FC", margin: "4px 8px" }} />
                          )}
                        </>
                      )}
                      {otherGroup.map((f) => renderRow(f, false))}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Separator */}
          <p className={classes.orDivider}>or</p>

          {/* Drop zone */}
          <BrandKitDropZone
            type="font"
            accept=".ttf,.otf,.woff,.woff2"
            onFile={handleUploadFile}
            onFiles={handleUploadFontFiles}
            multiple
            uploading={uploading}
          />
        </>
      )}

      {/* Saved font presets */}
      {fontPresets.length > 0 && (
        <div className={classes.colorList} style={expanded ? { marginTop: 2 } : undefined}>
          <DndContext sensors={presetSensors} collisionDetection={closestCenter} onDragEnd={handlePresetDragEnd}>
            <SortableContext
              items={fontPresets.map((p: BrandKitFontPreset) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {fontPresets.map((preset: BrandKitFontPreset) => (
                <SortableFontPresetRow
                  key={preset.id}
                  preset={preset}
                  onApply={handleApplyPreset}
                  onEdit={handleEditPreset}
                  onRemove={handleRemovePreset}
                  onUpdateLabel={handleUpdatePresetLabel}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Add/Edit font preset modal */}
      <AddBrandFontModal
        open={modalOpen}
        anchorEl={modalAnchor}
        onClose={() => {
          setModalOpen(false);
          setEditingPreset(null);
        }}
        initialFamily={selectedFamilyForModal}
        editPreset={editingPreset}
        fontFamilies={allFontFamilies}
        onSave={handleSavePreset}
        onUpdate={handleUpdatePreset}
      />

      {/* Brand Fonts queue — lists every detected family after a font upload.
          Each row can be added to the Brand Kit (inline font name input →
          Save) or skipped. Rows disappear when added or skipped; the dialog
          auto-closes when empty. */}
      <BrandFontsQueueDialog
        open={brandFontQueueOpen}
        families={brandFontQueue}
        onClose={() => {
          setBrandFontQueueOpen(false);
          setBrandFontQueue([]);
        }}
        onSave={async (input) => {
          await addFontPreset(input);
          // Collapse the font dropdown + drop zone section once a preset has
          // been saved from the upload queue.
          setExpanded(false);
        }}
        onCancel={async (remaining) => {
          // Footer "Cancel" — roll back the upload for every family still
          // in the queue: remove from CDN registry, local storage, loaded
          // FontFaces, and Brand Kit font assets.
          const allVariants = remaining.flatMap((f) => f.variantNames);
          for (const fontName of allVariants) {
            try {
              await s3CustomFontService.removeFontFromRegistry(fontName);
            } catch (err) {
              console.warn(`Failed to remove ${fontName} from CDN registry:`, err);
            }
            try {
              await customFontStorage.deleteCustomFont(fontName);
            } catch (err) {
              console.warn(`Failed to remove ${fontName} from local storage:`, err);
            }
            const loaded = Array.from(document.fonts).find((f) => f.family.toLowerCase() === fontName.toLowerCase());
            if (loaded) document.fonts.delete(loaded);
          }
          const fontsItems = useBrandKitStore.getState().fonts.items as Array<{ id: string; name: string }>;
          const lowerVariants = new Set(allVariants.map((n) => n.toLowerCase()));
          const toRemove = fontsItems.filter((item) => lowerVariants.has((item.name || "").toLowerCase()));
          for (const item of toRemove) {
            try {
              await useBrandKitStore.getState().removeAsset("font", item.id);
            } catch (err) {
              console.warn(`Failed to remove Brand Kit font asset ${item.name}:`, err);
            }
          }
          s3CustomFontService.clearCache();
          await loadBrandKit();
          window.dispatchEvent(new Event("custom-fonts-updated"));
        }}
      />
    </div>
  );
};

// ─── BrandImagesSection ───────────────────────────────────────────────────────

const BrandImagesSection = () => {
  const classes = useStyles();
  const editor = useEditor();
  const isEmptyState = useIsBuilderEmptyState();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = useContext(SnackBarContext);

  const {
    setImageState,
    setShowImagePanel,
    setShowIconsPanel,
    setShowQRcodePanel,
    setBrandKitAssetsPanel,
    currentFormatConfig,
  } = useStore();
  const { images, addAsset, removeAsset, loadBrandKit } = useBrandKitStore();

  const handlePlusClick = () => {
    setExpanded(!expanded);
  };

  // Trigger right sidebar image search with the query
  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    setImageState({ search: q });
    setShowImagePanel(true);
    setShowIconsPanel(false);
    setShowQRcodePanel(false);
    setBrandKitAssetsPanel(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  // Upload image to brand kit S3 storage
  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      startUpload("Saving image to Brand Kit...");
      try {
        const result = await uploadBrandKitAsset(file, "image");
        addAsset("image", {
          id: `temp-${Date.now()}`,
          name: result.name,
          s3Url: result.s3Url,
          s3Key: result.s3Key,
          mimeType: result.mimeType,
          fileSize: result.fileSize,
        });
        // Reload to get the server-assigned id
        await loadBrandKit();
      } catch (err) {
        console.error("Brand image upload failed:", err);
        runSnackBar({
          msg: "Failed to save image. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        setUploading(false);
        endUpload();
      }
    },
    [addAsset, loadBrandKit, startUpload, endUpload]
  );

  // Add a brand image to the canvas
  const handleImageClick = useCallback(
    async (item: any) => {
      if (!editor || isEmptyState) return;
      startUpload("Adding image to canvas...");
      try {
        const signedSrc = await getSignedS3Url(item.s3Url);
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = signedSrc;

          img.onload = () => {
            const imgW = img.naturalWidth;
            const imgH = img.naturalHeight;
            const MAX_DISPLAY = 1000;
            const scaleFactor = Math.max(imgW, imgH) > MAX_DISPLAY ? MAX_DISPLAY / Math.max(imgW, imgH) : 1;
            const displayW = Math.round(imgW * scaleFactor);
            const displayH = Math.round(imgH * scaleFactor);

            const assetId = AssetRecordType.createId();
            const { width, height } = editor.getViewportScreenBounds();
            const center = editor.screenToPage({ x: width / 2, y: height / 2 });

            editor.createAssets([
              {
                id: assetId,
                type: "image",
                typeName: "asset",
                props: {
                  name: item.name,
                  src: item.s3Url,
                  w: imgW,
                  h: imgH,
                  mimeType: item.mimeType || "image/png",
                  isAnimated: false,
                },
                meta: { name: item.name },
              },
            ]);

            const shapeId = `shape:brand-img-${Date.now()}` as TLShapeId;
            editor.createShapes([
              {
                id: shapeId,
                type: "customImage",
                props: { assetId, w: displayW, h: displayH },
                x: center.x - displayW / 2,
                y: center.y - displayH / 2,
              },
            ]);
            editor.setCurrentTool("select");
            editor.select(shapeId);
            resolve();
          };

          img.onerror = () => {
            console.error("Failed to load brand image:", signedSrc);
            reject(new Error("Failed to load brand image"));
          };
        });
      } catch (err) {
        console.error("Error adding image to canvas:", err);
        runSnackBar({
          msg: "Failed to add image to canvas. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [editor, isEmptyState, startUpload, endUpload]
  );

  const handleRemoveImage = useCallback(
    async (id: string) => {
      await removeAsset("image", id);
    },
    [removeAsset]
  );

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <span className={classes.sectionLabel}>Brand Images</span>
        <PlusIcon
          className={classes.plusIcon}
          onClick={handlePlusClick}
          style={{ transform: expanded ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
        />
      </div>

      {expanded && (
        <>
          {/* Search input */}
          <div className={classes.searchWrapper}>
            <MagnifyingGlassIcon className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              placeholder="Search images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          {/* Separator */}
          <p className={classes.orDivider}>or</p>

          {/* Drop zone */}
          <BrandKitDropZone type="image" accept="image/*,.pdf" onFile={handleUploadFile} uploading={uploading} />
        </>
      )}

      {/* Uploaded brand images gallery */}
      <BrandKitGallery
        items={images.items}
        onItemClick={handleImageClick}
        onRemove={handleRemoveImage}
        seeAllLabel="See all"
        onSeeAll={() => {
          setBrandKitAssetsPanel("image");
          setShowImagePanel(false);
          setShowIconsPanel(false);
          setShowQRcodePanel(false);
        }}
      />
    </div>
  );
};

// ─── BrandLogosSection ────────────────────────────────────────────────────────

const BrandLogosSection = () => {
  const classes = useStyles();
  const editor = useEditor();
  const isEmptyState = useIsBuilderEmptyState();
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = useContext(SnackBarContext);

  const { currentFormatConfig, setBrandKitAssetsPanel, setShowImagePanel, setShowIconsPanel, setShowQRcodePanel } =
    useStore();
  const { logos, addAsset, removeAsset, loadBrandKit } = useBrandKitStore();

  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      startUpload("Saving logo to Brand Kit...");
      try {
        const result = await uploadBrandKitAsset(file, "logo");
        addAsset("logo", {
          id: `temp-${Date.now()}`,
          name: result.name,
          s3Url: result.s3Url,
          s3Key: result.s3Key,
          mimeType: result.mimeType,
          fileSize: result.fileSize,
        });
        await loadBrandKit();
      } catch (err) {
        console.error("Logo upload failed:", err);
        runSnackBar({
          msg: "Failed to save logo. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        setUploading(false);
        endUpload();
      }
    },
    [addAsset, loadBrandKit, startUpload, endUpload]
  );

  const handleLogoClick = useCallback(
    async (item: any) => {
      if (!editor || isEmptyState) return;
      startUpload("Adding logo to canvas...");
      try {
        const signedSrc = await getSignedS3Url(item.s3Url);
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.src = signedSrc;

          img.onload = () => {
            const imgW = img.naturalWidth;
            const imgH = img.naturalHeight;
            const MAX_DISPLAY = 1000;
            const scaleFactor = Math.max(imgW, imgH) > MAX_DISPLAY ? MAX_DISPLAY / Math.max(imgW, imgH) : 1;
            const displayW = Math.round(imgW * scaleFactor);
            const displayH = Math.round(imgH * scaleFactor);

            const assetId = AssetRecordType.createId();
            const { width, height } = editor.getViewportScreenBounds();
            const center = editor.screenToPage({ x: width / 2, y: height / 2 });

            editor.createAssets([
              {
                id: assetId,
                type: "image",
                typeName: "asset",
                props: {
                  name: item.name,
                  src: item.s3Url,
                  w: imgW,
                  h: imgH,
                  mimeType: item.mimeType || "image/png",
                  isAnimated: false,
                },
                meta: { name: item.name },
              },
            ]);

            const shapeId = `shape:brand-logo-${Date.now()}` as TLShapeId;
            editor.createShapes([
              {
                id: shapeId,
                type: "customImage",
                props: { assetId, w: displayW, h: displayH },
                x: center.x - displayW / 2,
                y: center.y - displayH / 2,
              },
            ]);
            editor.setCurrentTool("select");
            editor.select(shapeId);
            resolve();
          };

          img.onerror = () => {
            console.error("Failed to load brand logo:", signedSrc);
            reject(new Error("Failed to load brand logo"));
          };
        });
      } catch (err) {
        console.error("Error adding logo to canvas:", err);
        runSnackBar({
          msg: "Failed to add logo to canvas. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [editor, isEmptyState, startUpload, endUpload]
  );

  const handleRemoveLogo = useCallback(
    async (id: string) => {
      await removeAsset("logo", id);
    },
    [removeAsset]
  );

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <span className={classes.sectionLabel}>Logos</span>
        <PlusIcon
          className={classes.plusIcon}
          onClick={() => setExpanded(!expanded)}
          style={{ transform: expanded ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
        />
      </div>

      {expanded && (
        <BrandKitDropZone type="logo" accept="image/*,.svg,.pdf" onFile={handleUploadFile} uploading={uploading} />
      )}

      {/* Uploaded brand logos gallery */}
      <BrandKitGallery
        items={logos.items}
        onItemClick={handleLogoClick}
        onRemove={handleRemoveLogo}
        seeAllLabel="See all"
        onSeeAll={() => {
          setBrandKitAssetsPanel("logo");
          setShowImagePanel(false);
          setShowIconsPanel(false);
          setShowQRcodePanel(false);
        }}
      />
    </div>
  );
};

// ─── BrandIconsSection ────────────────────────────────────────────────────────

const BrandIconsSection = () => {
  const classes = useStyles();
  const editor = useEditor();
  const isEmptyState = useIsBuilderEmptyState();
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const { startUpload, endUpload } = useImageUpload();
  const runSnackBar = useContext(SnackBarContext);

  const { setShowImagePanel, setShowIconsPanel, setShowQRcodePanel, setBrandKitAssetsPanel } = useStore();
  const { icons, addAsset, removeAsset, loadBrandKit } = useBrandKitStore();

  const handlePlusClick = () => {
    setExpanded(!expanded);
  };

  // Trigger right sidebar icon search with the query
  const handleSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    (window as any).__pendingIconSearch = q;
    setShowIconsPanel(true);
    setShowImagePanel(false);
    setShowQRcodePanel(false);
    setBrandKitAssetsPanel(null);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  // Upload icon to brand kit S3 storage
  const handleUploadFile = useCallback(
    async (file: File) => {
      setUploading(true);
      startUpload("Saving icon to Brand Kit...");
      try {
        const result = await uploadBrandKitAsset(file, "icon");
        addAsset("icon", {
          id: `temp-${Date.now()}`,
          name: result.name,
          s3Url: result.s3Url,
          s3Key: result.s3Key,
          mimeType: result.mimeType,
          fileSize: result.fileSize,
        });
        await loadBrandKit();
      } catch (err) {
        console.error("Brand icon upload failed:", err);
        runSnackBar({
          msg: "Failed to save icon. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        setUploading(false);
        endUpload();
      }
    },
    [addAsset, loadBrandKit, startUpload, endUpload]
  );

  // Add a brand icon to the canvas as customSvg (if SVG) or customImage
  const handleIconClick = useCallback(
    async (item: any) => {
      if (!editor || isEmptyState) return;
      startUpload("Adding icon to canvas...");
      try {
        const isSvg = item.mimeType === "image/svg+xml" || item.s3Url?.endsWith(".svg");

        if (isSvg) {
          // Fetch SVG content and create as customSvg
          const signedSvgUrl = await getSignedS3Url(item.s3Url);
          const res = await fetch(signedSvgUrl);
          const svgContent = await res.text();
          const { createShapeId } = await import("@tldraw/editor");
          const shapeId = createShapeId();
          const { width, height } = editor.getViewportScreenBounds();
          const center = editor.screenToPage({ x: width / 2, y: height / 2 });

          editor.createShape({
            id: shapeId,
            type: "customSvg",
            x: center.x - 125 / 2,
            y: center.y - 125 / 2,
            props: {
              w: 125,
              h: 125,
              svgPath: svgContent,
              fill: "#000000",
              colorMix: { "0": { color: "#000000", isHidden: false } },
            },
          });
          editor.setCurrentTool("select");
          editor.select(shapeId);
        } else {
          // Non-SVG icon: add as customImage
          const signedSrc = await getSignedS3Url(item.s3Url);
          await new Promise<void>((resolve, reject) => {
            const iconImg = new Image();
            iconImg.src = signedSrc;

            iconImg.onload = () => {
              const imgW = iconImg.naturalWidth;
              const imgH = iconImg.naturalHeight;
              const MAX_DISPLAY = 1000;
              const scaleFactor = Math.max(imgW, imgH) > MAX_DISPLAY ? MAX_DISPLAY / Math.max(imgW, imgH) : 1;
              const displayW = Math.round(imgW * scaleFactor);
              const displayH = Math.round(imgH * scaleFactor);

              const assetId = AssetRecordType.createId();
              const { width, height } = editor.getViewportScreenBounds();
              const center = editor.screenToPage({ x: width / 2, y: height / 2 });

              editor.createAssets([
                {
                  id: assetId,
                  type: "image",
                  typeName: "asset",
                  props: {
                    name: item.name,
                    src: item.s3Url,
                    w: imgW,
                    h: imgH,
                    mimeType: item.mimeType || "image/png",
                    isAnimated: false,
                  },
                  meta: { name: item.name },
                },
              ]);

              const shapeId = `shape:brand-icon-${Date.now()}` as TLShapeId;
              editor.createShapes([
                {
                  id: shapeId,
                  type: "customImage",
                  props: { assetId, w: displayW, h: displayH },
                  x: center.x - displayW / 2,
                  y: center.y - displayH / 2,
                },
              ]);
              editor.setCurrentTool("select");
              editor.select(shapeId);
              resolve();
            };

            iconImg.onerror = () => {
              console.error("Failed to load brand icon:", signedSrc);
              reject(new Error("Failed to load brand icon"));
            };
          });
        }
      } catch (err) {
        console.error("Error adding icon to canvas:", err);
        runSnackBar({
          msg: "Failed to add icon to canvas. Please try again.",
          type: "error",
          vertical: "top",
          horizontal: "center",
        });
      } finally {
        endUpload();
      }
    },
    [editor, isEmptyState, startUpload, endUpload]
  );

  const handleRemoveIcon = useCallback(
    async (id: string) => {
      await removeAsset("icon", id);
    },
    [removeAsset]
  );

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <span className={classes.sectionLabel}>Icons</span>
        <PlusIcon
          className={classes.plusIcon}
          onClick={handlePlusClick}
          style={{ transform: expanded ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
        />
      </div>

      {expanded && (
        <>
          {/* Search input */}
          <div className={classes.searchWrapper}>
            <MagnifyingGlassIcon className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          {/* Separator */}
          <p className={classes.orDivider}>or</p>

          {/* Drop zone */}
          <BrandKitDropZone type="icon" accept=".svg,image/*,.pdf" onFile={handleUploadFile} uploading={uploading} />
        </>
      )}

      {/* Uploaded brand icons gallery */}
      <BrandKitGallery
        items={icons.items}
        onItemClick={handleIconClick}
        onRemove={handleRemoveIcon}
        seeAllLabel="See all"
        onSeeAll={() => {
          setBrandKitAssetsPanel("icon");
          setShowImagePanel(false);
          setShowIconsPanel(false);
          setShowQRcodePanel(false);
        }}
      />
    </div>
  );
};

// ─── BrandKit Component ───────────────────────────────────────────────────────

const BrandKitComponent = () => {
  const classes = useStyles();
  const editor = useEditor();
  const [addingColor, setAddingColor] = useState(false);
  const [newColor, setNewColor] = useState("#000000");
  const [newColorAnchor, setNewColorAnchor] = useState<HTMLElement | null>(null);

  const { openColorPicker, closeColorPicker, isOpen } = useColorPickerContext();
  const newPickerId = "brand-color-new";

  const { colors, isLoading, loadBrandKit, addColor, updateColor, removeColor, reorderColors } = useBrandKitStore();

  useEffect(() => {
    loadBrandKit();
  }, []);

  // Close new color picker if another opens
  useEffect(() => {
    if (!isOpen(newPickerId) && newColorAnchor) {
      setNewColorAnchor(null);
      setAddingColor(false);
    }
  }, [isOpen, newColorAnchor]);

  const handleAddClick = (e: React.MouseEvent<SVGElement>) => {
    // Hard-stop once the user has saved the maximum number of brand
    // colors — the icon is also greyed-out in the UI, but we guard
    // here too in case the click slips through (e.g. keyboard).
    if (colors.length >= MAX_BRAND_COLORS) return;
    setNewColor("#000000");
    setNewColorAnchor(e.currentTarget as unknown as HTMLElement);
    setAddingColor(true);
    openColorPicker(newPickerId);
  };

  const handleNewColorClose = async () => {
    if (newColor) {
      const hex6 = newColor.length > 7 ? newColor.slice(0, 7) : newColor;
      await addColor({ hexValue: hex6 });
    }
    setNewColorAnchor(null);
    setAddingColor(false);
    closeColorPicker(newPickerId);
  };

  const handleShuffleColors = useCallback(async () => {
    if (colors.length < 2) return;

    // Record old hex order before shuffle
    const oldHexes = colors.map((c) => c.hexValue.toLowerCase());

    // Fisher-Yates shuffle on IDs
    const ids = colors.map((c) => c.id);
    for (let i = ids.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ids[i], ids[j]] = [ids[j], ids[i]];
    }

    // Compute new hex order after shuffle
    const colorById = new Map(colors.map((c) => [c.id, c.hexValue.toLowerCase()]));
    const newHexes = ids.map((id) => colorById.get(id)!);

    // Build position-based color mapping (oldHex → newHex)
    const colorMap = new Map<string, string>();
    for (let i = 0; i < oldHexes.length; i++) {
      if (oldHexes[i] !== newHexes[i]) {
        colorMap.set(oldHexes[i], newHexes[i]);
      }
    }

    // Update shapes on canvas that use any of the shuffled colors
    // Use editor.run with ignoreShapeLock so locked shapes (front/back backgrounds) also update
    if (colorMap.size > 0) {
      editor.run(
        () => {
          const allShapes = editor.getCurrentPageShapes();

          for (const shape of allShapes) {
            const props = { ...(shape as any).props };
            let changed = false;

            // Direct color properties
            if (typeof props.fill === "string" && colorMap.has(props.fill.toLowerCase())) {
              props.fill = colorMap.get(props.fill.toLowerCase());
              changed = true;
            }
            if (typeof props.outlineColor === "string" && colorMap.has(props.outlineColor.toLowerCase())) {
              props.outlineColor = colorMap.get(props.outlineColor.toLowerCase());
              changed = true;
            }

            // colorMix (object with indexed entries, each has .color)
            if (props.colorMix && typeof props.colorMix === "object") {
              const updatedMix = { ...props.colorMix };
              let mixChanged = false;
              for (const key of Object.keys(updatedMix)) {
                if (updatedMix[key]?.color && colorMap.has(updatedMix[key].color.toLowerCase())) {
                  updatedMix[key] = { ...updatedMix[key], color: colorMap.get(updatedMix[key].color.toLowerCase()) };
                  mixChanged = true;
                }
              }
              if (mixChanged) {
                props.colorMix = updatedMix;
                changed = true;
              }
            }

            // outlineColorMix
            if (props.outlineColorMix && typeof props.outlineColorMix === "object") {
              const updatedMix = { ...props.outlineColorMix };
              let mixChanged = false;
              for (const key of Object.keys(updatedMix)) {
                if (updatedMix[key]?.color && colorMap.has(updatedMix[key].color.toLowerCase())) {
                  updatedMix[key] = { ...updatedMix[key], color: colorMap.get(updatedMix[key].color.toLowerCase()) };
                  mixChanged = true;
                }
              }
              if (mixChanged) {
                props.outlineColorMix = updatedMix;
                changed = true;
              }
            }

            // outShadowArray (each shadow has .color)
            if (props.outShadowArray && typeof props.outShadowArray === "object") {
              const updatedShadows = { ...props.outShadowArray };
              let shadowChanged = false;
              for (const key of Object.keys(updatedShadows)) {
                if (updatedShadows[key]?.color && colorMap.has(updatedShadows[key].color.toLowerCase())) {
                  updatedShadows[key] = {
                    ...updatedShadows[key],
                    color: colorMap.get(updatedShadows[key].color.toLowerCase()),
                  };
                  shadowChanged = true;
                }
              }
              if (shadowChanged) {
                props.outShadowArray = updatedShadows;
                changed = true;
              }
            }

            // inShadowArray (each shadow has .color)
            if (props.inShadowArray && typeof props.inShadowArray === "object") {
              const updatedShadows = { ...props.inShadowArray };
              let shadowChanged = false;
              for (const key of Object.keys(updatedShadows)) {
                if (updatedShadows[key]?.color && colorMap.has(updatedShadows[key].color.toLowerCase())) {
                  updatedShadows[key] = {
                    ...updatedShadows[key],
                    color: colorMap.get(updatedShadows[key].color.toLowerCase()),
                  };
                  shadowChanged = true;
                }
              }
              if (shadowChanged) {
                props.inShadowArray = updatedShadows;
                changed = true;
              }
            }

            if (changed) {
              editor.updateShape({ ...shape, props });
            }
          }
        },
        { ignoreShapeLock: true }
      );
    }

    // Persist the new order
    await reorderColors(ids);
  }, [colors, reorderColors, editor]);

  const handleUpdateColor = async (id: string, hexValue: string) => {
    const hex6 = hexValue.length > 7 ? hexValue.slice(0, 7) : hexValue;

    // Capture old hex before updating
    const oldColor = colors.find((c) => c.id === id);
    const oldHex = oldColor?.hexValue?.toLowerCase();

    await updateColor({ id, hexValue: hex6 });

    // Update all shapes on the canvas that use the old color
    // Use editor.run with ignoreShapeLock so locked shapes (front/back backgrounds) also update
    if (oldHex && oldHex !== hex6.toLowerCase()) {
      const newHex = hex6.toLowerCase();
      editor.run(
        () => {
          const allShapes = editor.getCurrentPageShapes();

          for (const shape of allShapes) {
            const props = { ...(shape as any).props };
            let changed = false;

            // Direct color properties
            if (typeof props.fill === "string" && props.fill.toLowerCase() === oldHex) {
              props.fill = newHex;
              changed = true;
            }
            if (typeof props.outlineColor === "string" && props.outlineColor.toLowerCase() === oldHex) {
              props.outlineColor = newHex;
              changed = true;
            }

            // colorMix (object with indexed entries, each has .color)
            if (props.colorMix && typeof props.colorMix === "object") {
              const updatedMix = { ...props.colorMix };
              let mixChanged = false;
              for (const key of Object.keys(updatedMix)) {
                if (updatedMix[key]?.color?.toLowerCase() === oldHex) {
                  updatedMix[key] = { ...updatedMix[key], color: newHex };
                  mixChanged = true;
                }
              }
              if (mixChanged) {
                props.colorMix = updatedMix;
                changed = true;
              }
            }

            // outlineColorMix
            if (props.outlineColorMix && typeof props.outlineColorMix === "object") {
              const updatedMix = { ...props.outlineColorMix };
              let mixChanged = false;
              for (const key of Object.keys(updatedMix)) {
                if (updatedMix[key]?.color?.toLowerCase() === oldHex) {
                  updatedMix[key] = { ...updatedMix[key], color: newHex };
                  mixChanged = true;
                }
              }
              if (mixChanged) {
                props.outlineColorMix = updatedMix;
                changed = true;
              }
            }

            // outShadowArray (each shadow has .color)
            if (props.outShadowArray && typeof props.outShadowArray === "object") {
              const updatedShadows = { ...props.outShadowArray };
              let shadowChanged = false;
              for (const key of Object.keys(updatedShadows)) {
                if (updatedShadows[key]?.color?.toLowerCase() === oldHex) {
                  updatedShadows[key] = { ...updatedShadows[key], color: newHex };
                  shadowChanged = true;
                }
              }
              if (shadowChanged) {
                props.outShadowArray = updatedShadows;
                changed = true;
              }
            }

            // inShadowArray (each shadow has .color)
            if (props.inShadowArray && typeof props.inShadowArray === "object") {
              const updatedShadows = { ...props.inShadowArray };
              let shadowChanged = false;
              for (const key of Object.keys(updatedShadows)) {
                if (updatedShadows[key]?.color?.toLowerCase() === oldHex) {
                  updatedShadows[key] = { ...updatedShadows[key], color: newHex };
                  shadowChanged = true;
                }
              }
              if (shadowChanged) {
                props.inShadowArray = updatedShadows;
                changed = true;
              }
            }

            if (changed) {
              editor.updateShape({ ...shape, props });
            }
          }
        },
        { ignoreShapeLock: true }
      );
    }
  };

  const handleRemoveColor = async (id: string) => {
    await removeColor(id);
  };

  const handleNewPickerChange = useCallback((color: string) => {
    setNewColor(color.toLowerCase());
  }, []);

  const newColorOpen = Boolean(newColorAnchor) && isOpen(newPickerId);

  // Drag-by-border + canvas-pan + outside-click-close behaviour for
  // the "new color" picker. The Paper's padded frame acts as the
  // drag handle.
  const {
    paperContentRef: newColorPaperRef,
    handleBorderMouseDown: handleNewColorBorderMouseDown,
    isDragging: isNewColorDragging,
  } = useDraggableColorPicker(newColorOpen, newColorAnchor, handleNewColorClose);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={classes.root} data-tour="brandkit-panel">
      <h2 className={classes.title} data-tour="brandkit-title">
        Brand Kit
      </h2>

      <div className={classes.content}>
        {/* ── Brand Colors ── */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionLabel}>Brand Colors</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {colors.length >= 2 && (
                <Tooltip
                  title="Shuffle colors across your design"
                  arrow
                  placement="top"
                  classes={{ tooltip: classes.customTooltip, arrow: classes.customTooltipArrow }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className={classes.plusIcon}
                    style={{ width: 16, height: 16 }}
                    onClick={handleShuffleColors}
                  >
                    <path
                      d="M11.5581 11.4766L12.6245 12.5439V9.95996H12.7085V12.71H9.9585V12.626H12.5757L11.5083 11.5596L9.8335 9.88379L9.89893 9.81836L11.5581 11.4766ZM12.7085 3.29297V6.04297H12.6245V3.42578L11.5581 4.49316L3.59912 12.4502L3.55029 12.4004L11.5083 4.44336L12.5757 3.37598H9.9585V3.29297H12.7085ZM6.16553 6.11719L6.11572 6.16699L3.55029 3.60059L3.59912 3.55176L6.16553 6.11719Z"
                      fill="currentColor"
                      stroke="currentColor"
                      strokeWidth="1.25"
                    />
                  </svg>
                </Tooltip>
              )}
              {(() => {
                // Render the "+" with the same tooltip style as the
                // shuffle button beside it. When the cap is hit the
                // icon switches to a disabled grey (#AEAFC7) + the
                // tooltip explains why; the click handler also early-
                // returns in that case.
                const atMax = colors.length >= MAX_BRAND_COLORS;
                const plusIcon = (
                  <PlusIcon
                    className={classes.plusIcon}
                    onClick={handleAddClick as any}
                    style={atMax ? { color: "#AEAFC7", cursor: "not-allowed" } : undefined}
                    aria-disabled={atMax}
                  />
                );
                return atMax ? (
                  <Tooltip
                    title={`You can only have up to ${MAX_BRAND_COLORS} brand colors.`}
                    arrow
                    placement="top"
                    classes={{ tooltip: classes.customTooltip, arrow: classes.customTooltipArrow }}
                  >
                    {/* Tooltip needs a focusable child for hover to
                        trigger reliably; Radix's PlusIcon is an SVG
                        which works fine but wrapping a span makes the
                        hover area + cursor consistent with the
                        disabled affordance. */}
                    <span style={{ display: "inline-flex" }}>{plusIcon}</span>
                  </Tooltip>
                ) : (
                  plusIcon
                );
              })()}
            </div>
          </div>

          {isLoading ? (
            <p className={classes.hint}>Loading...</p>
          ) : (
            <div className={classes.colorList}>
              {colors.map((color) => (
                <BrandColorRow
                  key={color.id}
                  id={color.id}
                  hexValue={color.hexValue}
                  label={color.label}
                  onUpdate={handleUpdateColor}
                  onRemove={handleRemoveColor}
                />
              ))}
            </div>
          )}

          {/* New color popover */}
          <Popover
            open={newColorOpen}
            anchorEl={newColorAnchor}
            onClose={handleNewColorClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            // Canvas-pannable + drag-by-border (see BrandColorRow's
            // popover above for the rationale).
            style={{ pointerEvents: "none" }}
            hideBackdrop
            disableScrollLock
            PaperProps={{
              style: {
                pointerEvents: "auto",
                borderRadius: "8px",
                transform: "translate(calc(8px + var(--cp-drag-x, 0px)), var(--cp-drag-y, 0px))",
              },
            }}
          >
            <div
              ref={newColorPaperRef}
              onMouseDown={handleNewColorBorderMouseDown}
              className="p-4 rounded-[8px] w-[264px] h-[442px] overflow-y-auto"
              // alignContent centers the inner picker vertically; see
              // ColorPickerPopover for the rationale.
              style={{ cursor: isNewColorDragging ? "grabbing" : "grab", alignContent: "center" }}
            >
              <CustomColorPicker
                editor={editor}
                shapeColor={newColor}
                showColorPickerSetMode
                onColorChange={handleNewPickerChange}
              />
            </div>
          </Popover>
        </div>

        {/* ── Fonts ── */}
        <BrandFontsSection />

        {/* ── Brand Images ── */}
        <BrandImagesSection />

        {/* ── Logos ── */}
        <BrandLogosSection />

        {/* ── Icons ── */}
        <BrandIconsSection />
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  customTooltip: {
    backgroundColor: "#0C172C",
    color: "#FFFFFF",
    fontFamily: '"Inter", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: "20px",
    maxWidth: 300,
    padding: "8px 16px",
    borderRadius: 4,
  },
  customTooltipArrow: {
    color: "#0C172C",
  },
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#0C172C",
    margin: "0 0 16px 0",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    overflowY: "auto" as any,
    overflowX: "hidden" as any,
    scrollbarWidth: "none" as any,
    msOverflowStyle: "none" as any,
    "&::-webkit-scrollbar": { display: "none" },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#2E3860",
  },
  plusIcon: {
    width: 18,
    height: 18,
    cursor: "pointer",
    color: "#2E3860",
  },
  colorList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  colorRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  swatchBtn: {
    minWidth: 16,
    minHeight: 16,
    width: 16,
    height: 16,
    borderRadius: 4,
    border: "1px solid #E1E6EB",
    cursor: "pointer",
    flexShrink: 0,
    marginRight: 6,
  },
  minusIcon: {
    width: 18,
    height: 18,
    cursor: "pointer",
    color: "#2E3860",
    flexShrink: 0,
  },
  dropdownWrapper: {
    position: "relative" as any,
  },
  dropdownBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "6px 8px",
    fontSize: 12,
    color: "#2E3860",
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": { borderColor: "#B0B0E8" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  chevron: {
    transition: "transform 0.15s",
    flexShrink: 0,
  },
  dropdownList: {
    position: "absolute" as any,
    top: "100%",
    left: 0,
    right: 0,
    marginTop: 2,
    background: "white",
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    maxHeight: 220,
    overflowY: "auto" as any,
    zIndex: 10,
    boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#C4C4D4", borderRadius: 4 },
  },
  dropdownItem: {
    display: "block",
    width: "100%",
    padding: "6px 8px",
    fontSize: 12,
    color: "#2E3860",
    background: "transparent",
    border: "none",
    textAlign: "left" as any,
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as any,
    "&:hover": { background: "#E2E2FC" },
  },
  orDivider: {
    fontSize: 11,
    color: "#A0A0B8",
    textAlign: "center" as any,
    margin: "2px 0",
  },
  uploadFontBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "6px 8px",
    fontSize: 11,
    fontWeight: 600,
    color: "#2E3860",
    background: "#F9F9FF",
    border: "1px dashed #C4C4D4",
    borderRadius: 4,
    cursor: "pointer",
    "&:hover": { background: "#E2E2FC" },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  hint: {
    fontSize: 11,
    color: "#707087",
    padding: "4px 0",
    margin: 0,
  },
  searchWrapper: {
    position: "relative" as any,
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute" as any,
    left: 8,
    width: 14,
    height: 14,
    color: "#707087",
    pointerEvents: "none" as any,
  },
  searchInput: {
    width: "100%",
    height: 28,
    padding: "0 8px 0 26px",
    fontSize: 10,
    color: "#2E3860",
    border: "1px solid #E2E2FC",
    borderRadius: 4,
    outline: "none",
    "&:focus": { borderColor: "#B0B0E8" },
    "&::placeholder": { color: "#A0A0B8" },
  },
});

export const BrandKit = React.memo(BrandKitComponent);
